import PropTypes from 'prop-types'

import CloseIcon from '@mui/icons-material/Close'

import Box from '@mui/material/Box'
import CardActionArea from '@mui/material/CardActionArea'
import IconButton from '@mui/material/IconButton'
import { alpha } from '@mui/material/styles'

import useFormatMessage from '@hooks/useFormatMessage'
import { glassChrome, iconTile } from '@utils/StylesHelper'

NotificationCard.propTypes = {
  /** Test id the card and its parts are keyed from */
  testId: PropTypes.string.isRequired,

  /** Avatar or icon tile shown at the start of the row */
  leading: PropTypes.node,

  /** The line that says what happened */
  title: PropTypes.node.isRequired,

  /** Dismisses the notification */
  onClose: PropTypes.func.isRequired,

  /** Router link props, for a notification that opens something when pressed */
  actionProps: PropTypes.object,

  /** What the notification has to say */
  children: PropTypes.node,
}

/**
 * The shape every notification takes: something to recognise it by, what happened, and
 * a way to send it away. Made of the app's glass, because a notification arrives over
 * the app rather than in it.
 *
 * Takes a ref because it is handed straight to a `Snackbar`, which grows it into view
 * and needs to hold the element to do it.
 *
 * @param {object} props - The component props.
 * @returns {JSX.Element} The rendered notification.
 */
export function NotificationCard({ testId, leading, title, onClose, actionProps, children, ref }) {
  const formatMessage = useFormatMessage()
  const Root = actionProps ? CardActionArea : Box

  return (
    <Root
      ref={ref}
      {...actionProps}
      sx={styles.card}
      data-testid={testId}
      aria-labelledby={`${testId}-title`}
      aria-describedby={`${testId}-description`}
    >
      {leading}

      <Box sx={styles.body}>
        <Box sx={styles.header}>
          {title}
          {/* Sits in the row rather than floating in the corner, so it cannot land on
              the text when the title runs long */}
          <IconButton
            size="small"
            edge="end"
            onClick={(e) => {
              e.preventDefault()
              onClose()
            }}
            data-testid={`${testId}-close`}
            aria-label={formatMessage('notifications.toast.close')}
            sx={styles.close}
          >
            <CloseIcon fontSize="inherit" aria-hidden="true" />
          </IconButton>
        </Box>

        {children}
      </Box>
    </Root>
  )
}

NotificationIcon.propTypes = {
  /** The mark itself */
  icon: PropTypes.elementType.isRequired,

  /** Palette colour the tile is tinted and the icon is drawn in */
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error', 'info']).isRequired,
}

/** A notification with no face to show wears a mark instead. */
export function NotificationIcon({ icon: Icon, color }) {
  return (
    <Box sx={styles.icon(color)} aria-hidden="true">
      <Icon fontSize="small" />
    </Box>
  )
}

export const styles = {
  card: (theme) => ({
    ...glassChrome(theme),
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
    width: '100%',
    minWidth: 280,
    maxWidth: 400,
    p: 1.5,
    borderRadius: 3,
    textAlign: 'left',
    color: 'text.primary',
  }),
  body: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 },
  header: { display: 'flex', alignItems: 'center', gap: 1 },
  title: { fontWeight: 'bold', flex: 1, minWidth: 0 },
  supporting: { color: 'text.secondary' },
  preWrap: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  close: { color: 'text.secondary', flexShrink: 0 },
  // Sized to the avatar the other notifications lead with, so they all start on the
  // same line whether the sender has a face or not
  icon: (color) => ({
    ...iconTile(40),
    backgroundColor: (theme) => alpha(theme.palette[color].main, 0.12),
    color: `${color}.main`,
  }),
  message: {
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',

    '@supports (-webkit-line-clamp: 2)': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'initial',
      display: '-webkit-box',
      WebkitLineClamp: '2',
      WebkitBoxOrient: 'vertical',
    },
  },
}
